import React, { useState } from 'react';
import { useStore } from '../../stores/useStore';

type Mode = 'login' | 'signup';

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '13px 14px',
    backgroundColor: 'white',
    border: '1px solid #c4b8a5',
    borderRadius: '10px',
    fontSize: '15px',
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
    boxSizing: 'border-box',
    color: '#2c2c2c',
};

const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#5a5a5a',
    display: 'block',
    marginBottom: '6px',
};

export const LoginPage: React.FC = () => {
    const { signIn, signUp } = useStore();
    const [mode, setMode] = useState<Mode>('login');

    // 로그인
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    // 회원가입
    const [signupName, setSignupName] = useState('');
    const [signupPhone, setSignupPhone] = useState('');
    const [signupPassword, setSignupPassword] = useState('');
    const [signupError, setSignupError] = useState('');
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [signupLoading, setSignupLoading] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 10 || !password.trim()) return;
        setLoginLoading(true);
        setLoginError('');
        const err = await signIn(`${digits}@beomyang.local`, password);
        if (err) setLoginError(err === 'Invalid login credentials' ? '전화번호 또는 비밀번호가 올바르지 않습니다.' : err);
        setLoginLoading(false);
    };

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!signupName.trim() || signupPhone.replace(/\D/g, '').length < 10 || signupPassword.length < 6) return;
        setSignupLoading(true);
        setSignupError('');
        const err = await signUp(signupName, signupPhone, signupPassword);
        if (err) {
            setSignupError(err.includes('already registered') ? '이미 등록된 전화번호입니다.' : err);
        } else {
            setSignupSuccess(true);
        }
        setSignupLoading(false);
    };

    const loginReady = phone.replace(/\D/g, '').length >= 10 && password.trim().length > 0;
    const signupReady = signupName.trim().length > 0 && signupPhone.replace(/\D/g, '').length >= 10 && signupPassword.length >= 6;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            backgroundColor: '#d8d0c5',
            maxWidth: '480px',
            margin: '0 auto',
            borderLeft: '1px solid #b8b0a5',
            borderRight: '1px solid #b8b0a5',
        }}>
            <header style={{
                backgroundColor: '#1e3a5f',
                color: 'white',
                padding: '20px 16px',
                borderBottom: '3px solid #c75c2e',
            }}>
                <h1 style={{ fontSize: '20px', fontWeight: 700, letterSpacing: '2px', fontFamily: "'Noto Sans KR', sans-serif" }}>
                    범양공조
                </h1>
                <p style={{ fontSize: '11px', color: '#a0b4c8', marginTop: '3px', letterSpacing: '1px' }}>
                    HVAC MANAGEMENT SYSTEM
                </p>
            </header>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
                <div style={{ width: '100%', backgroundColor: '#f4f1ea', border: '1px solid #c4b8a5', borderRadius: '16px', overflow: 'hidden' }}>
                    {/* 탭 */}
                    <div style={{ display: 'flex', borderBottom: '2px solid #c4b8a5' }}>
                        {(['login', 'signup'] as Mode[]).map((m) => (
                            <button
                                key={m}
                                onClick={() => { setMode(m); setLoginError(''); setSignupError(''); setSignupSuccess(false); }}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    border: 'none',
                                    backgroundColor: mode === m ? 'white' : '#f4f1ea',
                                    fontSize: '14px',
                                    fontWeight: mode === m ? 700 : 400,
                                    color: mode === m ? '#1e3a5f' : '#5a5a5a',
                                    cursor: 'pointer',
                                    borderBottom: mode === m ? '2px solid #c75c2e' : '2px solid transparent',
                                    fontFamily: "'Noto Sans KR', sans-serif",
                                }}
                            >
                                {m === 'login' ? '로그인' : '회원가입'}
                            </button>
                        ))}
                    </div>

                    {/* 로그인 폼 */}
                    {mode === 'login' && (
                        <form onSubmit={handleLogin} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={labelStyle}>전화번호</label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="010-0000-0000"
                                    style={inputStyle}
                                    autoComplete="tel"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>비밀번호</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="비밀번호 입력"
                                    style={inputStyle}
                                    autoComplete="current-password"
                                />
                            </div>
                            {loginError && (
                                <div style={{ padding: '10px 12px', backgroundColor: '#ffebee', border: '1px solid #c75c2e', borderRadius: '10px', fontSize: '13px', color: '#c75c2e' }}>
                                    {loginError}
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loginLoading || !loginReady}
                                style={{
                                    width: '100%', padding: '14px',
                                    backgroundColor: (loginLoading || !loginReady) ? '#c4b8a5' : '#c75c2e',
                                    color: 'white', border: 'none', borderRadius: '10px',
                                    fontSize: '15px', fontWeight: 700,
                                    cursor: (loginLoading || !loginReady) ? 'not-allowed' : 'pointer',
                                    fontFamily: "'Noto Sans KR', sans-serif", marginTop: '4px',
                                }}
                            >
                                {loginLoading ? '로그인 중...' : '로그인'}
                            </button>
                        </form>
                    )}

                    {/* 회원가입 폼 */}
                    {mode === 'signup' && (
                        <form onSubmit={handleSignup} style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {signupSuccess ? (
                                <div style={{ padding: '16px', backgroundColor: '#e8f5e9', border: '1px solid #5a7a6a', borderRadius: '10px', fontSize: '14px', color: '#5a7a6a', textAlign: 'center', lineHeight: 1.6 }}>
                                    <p style={{ fontWeight: 700, marginBottom: '4px' }}>가입이 완료되었습니다</p>
                                    <p style={{ fontSize: '12px' }}>로그인 탭에서 로그인하세요</p>
                                    <button
                                        type="button"
                                        onClick={() => setMode('login')}
                                        style={{
                                            marginTop: '12px', padding: '10px 24px',
                                            backgroundColor: '#1e3a5f', color: 'white',
                                            border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                                        }}
                                    >
                                        로그인하기
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label style={labelStyle}>이름 *</label>
                                        <input
                                            type="text"
                                            value={signupName}
                                            onChange={(e) => setSignupName(e.target.value)}
                                            placeholder="홍길동"
                                            style={inputStyle}
                                            autoFocus
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>전화번호 *</label>
                                        <input
                                            type="tel"
                                            value={signupPhone}
                                            onChange={(e) => setSignupPhone(e.target.value)}
                                            placeholder="010-0000-0000"
                                            style={inputStyle}
                                            autoComplete="tel"
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>비밀번호 * (6자 이상)</label>
                                        <input
                                            type="password"
                                            value={signupPassword}
                                            onChange={(e) => setSignupPassword(e.target.value)}
                                            placeholder="6자 이상 입력"
                                            style={inputStyle}
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {signupError && (
                                        <div style={{ padding: '10px 12px', backgroundColor: '#ffebee', border: '1px solid #c75c2e', borderRadius: '10px', fontSize: '13px', color: '#c75c2e' }}>
                                            {signupError}
                                        </div>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={signupLoading || !signupReady}
                                        style={{
                                            width: '100%', padding: '14px',
                                            backgroundColor: (signupLoading || !signupReady) ? '#c4b8a5' : '#1e3a5f',
                                            color: 'white', border: 'none', borderRadius: '10px',
                                            fontSize: '15px', fontWeight: 700,
                                            cursor: (signupLoading || !signupReady) ? 'not-allowed' : 'pointer',
                                            fontFamily: "'Noto Sans KR', sans-serif", marginTop: '4px',
                                        }}
                                    >
                                        {signupLoading ? '가입 중...' : '회원가입'}
                                    </button>
                                </>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
